import React, { useMemo, useState } from "react";
import { TextExtractor, findFirstComponentOfType } from "../../internal/TextExtractor";
import { useParser } from "../../../state/parser-hook";

interface ParseHtmlProps {
    children: React.ReactNode,
    html?: string,
}

/***
 * Parses HTML content using the parser hook.
 * The HTML content is expected to be a string. If not provided, the HTML content is expected to be in the arg-html child.
 * 
 * Params:
 *      children: The children, which can contain an arg-html child.
 *      html: The HTML content to parse. If not provided, the HTML content is expected to be in the arg-html child.
 */
export const ParseHtml = React.memo(function ParseHtml({ children, html }: ParseHtmlProps): React.ReactElement {
    const parser = useParser();

    const htmlChild = useMemo(() => findFirstComponentOfType(children, "arg-html"), [children]);
    const [htmlValue, setHtmlValue] = useState(html || "");

    const parsedHtml = useMemo(() => {
        if (htmlValue) {
            const parsedHtml = parser(htmlValue);
            return parsedHtml;
        }
        return "";
    }, [htmlValue, parser]);

    return <>
        {htmlChild && (
            <TextExtractor onExtractedText={setHtmlValue}>
                {htmlChild?.props.children}
            </TextExtractor>
        )}
        {parsedHtml}
    </>;
});